import { useSelector } from 'react-redux';
import { useContext } from 'react';
import {
  aggregateTaxAwarePrices,
  ClusivitySeparatedPrices,
  SubtotalRequests,
} from '@vp/om-tax-display';
import useSiteData from './useSiteData';
import type { RootState, FeeSubtotal } from '../types';
import { shippingOptionSelectors } from '../state/shippingOptions';
import { TaxDisplayContext } from '../components/shared/taxDisplay/TaxDisplayContext';
import { pricingSelectors } from '../state/pricing';

export default function usePricingSummary() {
  const { shippingPricingInCartType } = useSiteData();

  const {
    clusivitySeparatedItemPrices,
    clusivitySeparatedShippingPrices,
    taxDisplayContext,
  } = useContext(TaxDisplayContext) || {};

  // does the cart have a shipping method selected
  const hasSelectedShippingMethod = useSelector((state: RootState) =>
    shippingOptionSelectors.selectHasSelectedShippingMethod(
      state.shippingOptions
    )
  );
  // do shipping quotes exist
  const hasShippingOptionsToDisplay = useSelector((state: RootState) =>
    shippingOptionSelectors.selectHasShippingOptionsToDisplay(
      state.shippingOptions
    )
  );
  const cartContainsDeliverableProducts = useSelector((state: RootState) =>
    pricingSelectors.selectContainsDeliverableProducts(state.pricing)
  );
  const itemShippingPricingIdentifiers = useSelector((state: RootState) =>
    pricingSelectors.selectItemShippingPricingIdentifiers(state)
  ) as Record<string, string[]>;

  const shippingIsValid =
    hasSelectedShippingMethod && hasShippingOptionsToDisplay;

  // by default, we don't display shipping prices in cart
  let shippingPricingDisplayType = 'none';
  // if cart contains deliverable items...
  if (cartContainsDeliverableProducts) {
    shippingPricingDisplayType = shippingPricingInCartType;
  }

  const clusivitySeparatedPrices = Object.values(
    clusivitySeparatedItemPrices
  ).flat();
  const subtotalRequests: SubtotalRequests[] = [
    { key: 'product', level: 'order' },
  ];

  if (
    shippingPricingDisplayType === 'price' &&
    Object.values(clusivitySeparatedShippingPrices)
  ) {
    const flattenedClusivitySeparatedShippingPrices = Object.values(
      clusivitySeparatedShippingPrices
    ).flat();

    // if shipping prices are present, and we are displaying shipping pricing,
    // add them to the array of all clusivity separated prices
    clusivitySeparatedPrices.push(...flattenedClusivitySeparatedShippingPrices);
    subtotalRequests.push({ key: 'shipping', level: 'order' });
  }

  const aggregatedOrderPrice = aggregateTaxAwarePrices(
    clusivitySeparatedPrices as ClusivitySeparatedPrices<unknown>[],
    'order',
    'default',
    subtotalRequests,
    taxDisplayContext
  );

  const productPrice = aggregatedOrderPrice.subtotals.get('product');
  const shippingPrice = aggregatedOrderPrice.subtotals.get('shipping');

  // For now, we are replicating this logic from omt/component-library for determining what the
  //  total that we want to display in the order summary (future: extract this logic to tax-display library)
  const blockDisplayExcludedTaxes =
    aggregatedOrderPrice.excludedTaxes?.filter(
      tax => tax.displayType === 'block'
    ) || [];
  const displaySubtotalAsTotal =
    aggregatedOrderPrice.subtotals?.size === 1 &&
    blockDisplayExcludedTaxes.length === 0;
  const excludedTaxesCentAmount =
    blockDisplayExcludedTaxes.length > 0
      ? blockDisplayExcludedTaxes
          ?.map(tax => tax.centAmount)
          .reduce((prev, cur) => prev + cur) || 0
      : 0;
  // if we are only going to display the subtotal as the price, use the product subtotal tax aware discounted cents
  //  otherwise, use the order total tax aware discounted cents + any block excluded tax cent amounts
  const displayedOrderTotalCents = displaySubtotalAsTotal
    ? productPrice?.taxAwareDiscountedCents
    : aggregatedOrderPrice.taxAwareDiscountedCents + excludedTaxesCentAmount;

  // currently the only supported fees returned from tax display lib are shipping fees
  const shippingFees: FeeSubtotal[] = [
    ...aggregatedOrderPrice.fees.entries(),
  ].map(([key, fee]) => ({
    ...fee,
    rowLabelKey: key,
    applicableItems: itemShippingPricingIdentifiers[key] || [],
  }));

  return {
    displayedOrderTotalCents,
    aggregatedOrderPrice,
    productPrice,
    shippingPrice,
    shippingPricingDisplayType,
    shippingIsValid,
    shippingFees,
  };
}
